import { useMemo } from 'react';
import {
  InlineStack,
  Page,
  PageBody,
  PageHeader,
  PageHeaderIconTile,
  PageHeaderTabs,
  PageHeaderTitle,
  Stack,
  TabNav,
  TabNavList,
  TabNavTab,
} from '@customerio/pluma-components/react';
import { usePermissions } from '@customerio/hydra-permissions/react';
import { NewItemsStatus } from '@/components/new-items-status';
import { useLoadNew, useSettledRefetch, type NewItemsStatusState } from '@/hooks/use-load-new';
import { isEmptyString } from '@/utils/string';
import { DocumentTitle } from '@/components/page/document-title';
import {
  availableDeliveryChannels,
  type DeliveryFilterValues,
  DeliveryFilters,
  DeliveryTable,
  ExportDeliveriesButton,
  useDeliveryActions,
} from '@/features/deliveries';
import { useAuth } from '@/services/auth';
import { hasUnlimitedPlanFeatureAccess } from '@/services/auth/plan-features';
import { PlanFeature } from '@/services/auth/types';
import type { DeliveriesListParams, Delivery } from '@/services/db/delivery/entity';
import { useDeliveries, useNewDeliveriesCheck } from '@/services/db/delivery/queries';
import { buildDeliveriesListParams } from '@/services/db/delivery/utils';
import { requireWorkspaceId } from '@/services/db/shared/workspace';
import {
  buildEmptyMessage,
  hasActiveFilters,
  toExportInput,
  toFilterInput,
  useOutboxFilters,
  useSeedDefaultDateRange,
} from './hooks';
import type { OutboxTab } from './types';

type OutboxToolbarProps = {
  values: DeliveryFilterValues;
  onChange: (patch: Partial<DeliveryFilterValues>) => void;
  channels: readonly string[];
  isDrafts: boolean;
  workspaceId: number;
  canExport: boolean;
  /** The "load new" check status, shown before Export. */
  newCheckStatus: NewItemsStatusState;
};

/**
 * The filter bar plus the trailing toolbar actions (the "load new" check status + Export).
 * Drafts locks the status/user-action filters and caps the range at 30 days.
 */
function OutboxToolbar({
  values,
  onChange,
  channels,
  isDrafts,
  workspaceId,
  canExport,
  newCheckStatus,
}: OutboxToolbarProps) {
  return (
    <DeliveryFilters
      values={values}
      onChange={onChange}
      channels={channels}
      drafted={isDrafts}
      isFilteringByEmailEnabled={!isDrafts}
      fixed={isDrafts ? 'deliveryStatus,userActions' : undefined}
      maxRange={isDrafts ? '30 days' : undefined}
      dateRangeTooltipMessage={isDrafts ? 'Drafts are stored only for 30 days.' : undefined}
      actions={
        <InlineStack alignBlock="center" gap="200">
          <NewItemsStatus {...newCheckStatus} checkNowLabel="Check for new messages" />
          <ExportDeliveriesButton
            exportInput={{ ...toExportInput(values), drafted: isDrafts ? true : undefined }}
            from={isDrafts ? 'outbox_drafts' : 'outbox_deliveries'}
            workspaceId={workspaceId}
            isDisabled={!canExport}
          />
        </InlineStack>
      }
    />
  );
}

const MESSAGE_NOUN = { one: 'message', other: 'messages' } as const;

type LoadNewDeliveriesParams = {
  params: DeliveriesListParams;
  query: ReturnType<typeof useDeliveries>;
};

/**
 * The page's "load new" row and toolbar status for both tabs: the deliveries check (same params as
 * the list) wired into the shared `useLoadNew`.
 */
function useLoadNewDeliveries({ params, query }: LoadNewDeliveriesParams) {
  const { deliveries, firstPage, lastUpdated, isLoading, isFetching, isFetchingMore, refetch } = query;
  const check = useNewDeliveriesCheck({
    params,
    enabled: !isLoading && firstPage != null,
    seed: firstPage,
    seedUpdatedAt: lastUpdated,
  });

  const loadedIds = useMemo(() => new Set(deliveries.map((delivery) => delivery.id)), [deliveries]);
  const latestIds = useMemo(() => (check.data?.deliveries ?? []).map((delivery) => delivery.id), [check.data]);

  return useLoadNew({
    loadedIds,
    latestIds,
    latestHasMore: !isEmptyString(check.data?.continuation ?? ''),
    noun: MESSAGE_NOUN,
    list: { lastUpdated, isLoading, isFetching, isFetchingMore, refetch: useSettledRefetch(refetch) },
    check: { dataUpdatedAt: check.dataUpdatedAt, refetch: useSettledRefetch(check.refetch) },
  });
}

type OutboxPageProps = {
  /** Which tab is active — selects the drafts filter and the active tab. */
  tab: OutboxTab;
  /** Builds the href for each tab (the route owns the URL segment vocabulary). */
  tabHref: (tab: OutboxTab) => string;
  /** Builds a row's detail href (deliveries → React detail; drafts → Ember draft). */
  getDeliveryHref: (delivery: Delivery) => string;
};

/**
 * The Outbox page — the Deliveries/Sent (FPS-2591) and Drafts (FPS-2594) tabs,
 * both composed from the `features/deliveries` blocks. Ported from the Ember
 * `outbox/list` shell + `<cio-deliveries>`: one shared component parametrized by
 * tab. The route owns the URL segment vocabulary and passes the tab + the href
 * builders in.
 */
export default function OutboxPage({ tab, tabHref, getDeliveryHref }: OutboxPageProps) {
  const workspaceId = requireWorkspaceId();
  const { hasPermission } = usePermissions();
  // Match the Ember gates: export needs `journeys/exports/create`; the bulk send/retry/delete
  // actions need `journeys/messages/send` (its absence is the "read-only" state).
  const canExport = hasPermission('journeys/exports/create') === true;
  const canSendMessages = hasPermission('journeys/messages/send') === true;
  const { values: rawValues, onChange } = useOutboxFilters();
  // Apply the last-30-days default window when no range is set (Ember base-deliveries parity),
  // so the picker shows the window and the first query is already bounded.
  const values = useSeedDefaultDateRange(rawValues);
  const auth = useAuth();
  const isLiveNotificationsAvailable =
    auth.account != null && hasUnlimitedPlanFeatureAccess(auth.account, PlanFeature.LiveNotifications);
  const channels = availableDeliveryChannels(isLiveNotificationsAvailable);

  const isDrafts = tab === 'drafts';
  const params = buildDeliveriesListParams({ ...toFilterInput(values), drafts: isDrafts ? true : undefined });
  const deliveriesQuery = useDeliveries({ params });
  const { deliveries, isLoading, isError, refetch, hasMore, fetchMore, isFetchingMore } = deliveriesQuery;
  const { loadNew, status: newCheckStatus } = useLoadNewDeliveries({ params, query: deliveriesQuery });
  const actions = useDeliveryActions({ deliveries, deliveryStatus: values.deliveryStatus, workspaceId });

  return (
    <Page>
      <DocumentTitle title="Message activity" />
      <PageHeader>
        <PageHeaderIconTile name="delivery-log" />
        <PageHeaderTitle>Message activity</PageHeaderTitle>
        <PageHeaderTabs>
          <TabNav aria-label="Message activity">
            <TabNavList>
              <TabNavTab href={tabHref('deliveries')} isActive={tab === 'deliveries'}>
                Sent
              </TabNavTab>
              <TabNavTab href={tabHref('drafts')} isActive={tab === 'drafts'}>
                Drafts
              </TabNavTab>
            </TabNavList>
          </TabNav>
        </PageHeaderTabs>
      </PageHeader>
      <PageBody>
        <Stack gap="300">
          <OutboxToolbar
            values={values}
            onChange={onChange}
            channels={channels}
            isDrafts={isDrafts}
            workspaceId={workspaceId}
            canExport={canExport}
            newCheckStatus={newCheckStatus}
          />
          <DeliveryTable
            deliveries={deliveries}
            isLoading={isLoading}
            isError={isError}
            onReload={() => void refetch()}
            hasMore={hasMore}
            onLoadMore={() => void fetchMore()}
            isLoadingMore={isFetchingMore}
            loadNew={loadNew}
            getDeliveryHref={getDeliveryHref}
            actionType={values.actionType}
            hasFilters={hasActiveFilters(values)}
            emptyMessage={buildEmptyMessage(values, isDrafts, 'sent messages')}
            isDrafted={isDrafts}
            hasReadOnlyPermission={!canSendMessages}
            onBulkRetry={actions.onBulkRetry}
            onBulkSend={actions.onBulkSend}
            onBulkDelete={actions.onBulkDelete}
            onBulkRedeliver={actions.onBulkRedeliver}
            onRowRetry={actions.onRowRetry}
            onRowDelete={actions.onRowDelete}
          />
        </Stack>
      </PageBody>
    </Page>
  );
}
